/* =========================================================================
   Stanmoor — global.js
   Vanilla JS. No framework. Progressive enhancement over working HTML.
   ========================================================================= */
(function () {
  'use strict';

  const routes = window.Stanmoor && window.Stanmoor.routes ? window.Stanmoor.routes : {};
  const money = (cents) => {
    if (window.Stanmoor && typeof window.Stanmoor.formatMoney === 'function') {
      return window.Stanmoor.formatMoney(cents);
    }
    return '£' + (cents / 100).toFixed(2);
  };

  /* ----- Reveal on scroll ------------------------------------------------ */
  function initReveal() {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || document.documentElement.classList.contains('reduced-motion')) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ----- Generic drawer open/close -------------------------------------- */
  function bindDrawer(drawer) {
    if (!drawer) return;
    const open = () => {
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const focusable = drawer.querySelector('[data-drawer-focus], button, a, input');
      if (focusable) focusable.focus();
    };
    const close = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    };
    drawer.__open = open;
    drawer.__close = close;
    drawer.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', close));
    const overlay = drawer.querySelector('[data-drawer-overlay]');
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ----- Mobile nav ------------------------------------------------------ */
  function initNav() {
    const drawer = document.querySelector('[data-nav-drawer]');
    bindDrawer(drawer);
    document.querySelectorAll('[data-nav-toggle]').forEach((btn) =>
      btn.addEventListener('click', () => drawer && drawer.__open && drawer.__open())
    );
  }

  /* ----- Cart ------------------------------------------------------------ */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  bindDrawer(cartDrawer);

  function openCart() {
    if (cartDrawer && cartDrawer.__open) cartDrawer.__open();
  }

  async function fetchCart() {
    const res = await fetch(routes.cart_url ? routes.cart_url + '.js' : '/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  }

  function renderCart(cart) {
    // Badge
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    // Drawer body
    const body = document.querySelector('[data-cart-items]');
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML =
        '<div class="cart-empty"><p class="serif">' +
        (body.dataset.emptyText || 'Your cart is empty.') +
        '</p></div>';
    } else {
      body.innerHTML = cart.items
        .map((item) => {
          const img = item.image
            ? '<img src="' + item.image.replace(/(\.[^.]*)$/, '_160x$1') + '" alt="" width="64" loading="lazy">'
            : '';
          const opts =
            item.options_with_values && item.product_has_only_default_variant !== true
              ? '<p class="line-item__variant">' +
                item.options_with_values.map((o) => o.value).join(' · ') +
                '</p>'
              : '';
          return (
            '<div class="line-item" data-line-key="' + item.key + '">' +
            '<div class="line-item__media">' + img + '</div>' +
            '<div class="line-item__body">' +
            '<p class="line-item__title">' + item.product_title + '</p>' +
            opts +
            '<div class="quantity quantity--sm" data-quantity>' +
            '<button type="button" data-qty-down aria-label="Decrease">–</button>' +
            '<input type="text" inputmode="numeric" value="' + item.quantity + '" data-line-qty aria-label="Quantity">' +
            '<button type="button" data-qty-up aria-label="Increase">+</button>' +
            '</div>' +
            '<button type="button" class="line-item__remove" data-line-remove>Remove</button>' +
            '</div>' +
            '<div class="line-item__price">' + money(item.final_line_price) + '</div>' +
            '</div>'
          );
        })
        .join('');
    }
    // Subtotal
    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.textContent = money(cart.total_price);
    });
    document.querySelectorAll('[data-cart-foot]').forEach((el) => {
      el.hidden = cart.item_count === 0;
    });
    bindLineControls();
  }

  async function updateLine(key, quantity) {
    const res = await fetch(routes.cart_change_url ? routes.cart_change_url + '.js' : '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    const cart = await res.json();
    renderCart(cart);
  }

  function bindLineControls() {
    document.querySelectorAll('[data-cart-items] .line-item').forEach((row) => {
      const key = row.dataset.lineKey;
      const input = row.querySelector('[data-line-qty]');
      row.querySelector('[data-qty-down]')?.addEventListener('click', () =>
        updateLine(key, Math.max(0, parseInt(input.value, 10) - 1))
      );
      row.querySelector('[data-qty-up]')?.addEventListener('click', () =>
        updateLine(key, parseInt(input.value, 10) + 1)
      );
      row.querySelector('[data-line-remove]')?.addEventListener('click', () => updateLine(key, 0));
      input?.addEventListener('change', () =>
        updateLine(key, Math.max(0, parseInt(input.value, 10) || 0))
      );
    });
  }

  async function addToCart(form, button) {
    // Sold out / unavailable variants are marked aria-disabled rather than
    // disabled (so they stay focusable). Honour that here — otherwise the
    // click round-trips to /cart/add.js just to be rejected with a 422.
    if (button.getAttribute('aria-disabled') === 'true') return;

    const formData = new FormData(form);
    button.setAttribute('aria-disabled', 'true');
    const label = button.querySelector('[data-add-label]') || button;
    const original = label.textContent;
    label.textContent = 'Adding…';
    try {
      const res = await fetch(routes.cart_add_url ? routes.cart_add_url + '.js' : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) throw new Error('add failed');
      const cart = await fetchCart();
      renderCart(cart);
      openCart();
      label.textContent = original;
    } catch (e) {
      label.textContent = 'Try again';
      setTimeout(() => (label.textContent = original), 1600);
    } finally {
      button.removeAttribute('aria-disabled');
    }
  }

  function initCart() {
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) =>
      btn.addEventListener('click', (e) => {
        // Only intercept for drawer mode
        if (btn.dataset.cartMode === 'drawer') {
          e.preventDefault();
          openCart();
        }
      })
    );
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        const button = form.querySelector('[type="submit"]');
        // Block submission of a sold-out variant in both cart modes.
        if (button && button.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          return;
        }
        if (form.dataset.cartMode === 'page') return; // let it navigate
        e.preventDefault();
        addToCart(form, button);
      });
    });
    // Prime the badge on load
    fetchCart().then(renderCart).catch(() => {});
  }

  /* ----- Product: quantity + variants ----------------------------------- */
  function initQuantitySteppers() {
    document.querySelectorAll('[data-quantity]').forEach((q) => {
      const input = q.querySelector('input');
      if (!input) return;
      q.querySelector('[data-qty-down]')?.addEventListener('click', () => {
        input.value = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      q.querySelector('[data-qty-up]')?.addEventListener('click', () => {
        input.value = (parseInt(input.value, 10) || 1) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initVariants() {
    document.querySelectorAll('[data-variant-selects]').forEach((root) => {
      const dataEl = document.querySelector('[data-variant-json="' + root.dataset.variantSelects + '"]');
      if (!dataEl) return;
      let variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      const invEl = document.querySelector('[data-inventory-json="' + root.dataset.variantSelects + '"]');
      let inventory = {};
      if (invEl) {
        try {
          inventory = JSON.parse(invEl.textContent);
        } catch (e) {
          inventory = {};
        }
      }
      const stockNote = document.querySelector('[data-stock-note="' + root.dataset.variantSelects + '"]');

      function updateStockNote(variant) {
        if (!stockNote) return;
        const info = inventory[variant.id];
        const threshold = parseInt(stockNote.dataset.threshold, 10) || 0;
        // Untracked inventory has no meaningful count, so say nothing at all.
        if (!info || !info.tracked || !variant.available || info.qty <= 0 || info.qty > threshold) {
          stockNote.hidden = true;
          return;
        }
        const label = stockNote.querySelector('[data-stock-text]');
        if (label) {
          label.textContent =
            info.qty === 1
              ? stockNote.dataset.textOne
              : (stockNote.dataset.textOther || '').replace('__n__', info.qty);
        }
        stockNote.hidden = false;
      }

      const form = document.querySelector('[data-product-form="' + root.dataset.variantSelects + '"]');
      const idInput = form ? form.querySelector('[data-variant-id]') : null;
      const priceEl = document.querySelector('[data-current-price="' + root.dataset.variantSelects + '"]');
      const addBtn = form ? form.querySelector('[type="submit"] [data-add-label]') : null;
      const addBtnEl = form ? form.querySelector('[type="submit"]') : null;

      function currentOptions() {
        const opts = [];
        root.querySelectorAll('[data-option-index]').forEach((group) => {
          const checked = group.querySelector('input:checked') || group.querySelector('select');
          if (checked) opts[parseInt(group.dataset.optionIndex, 10)] = checked.value;
        });
        return opts;
      }

      function match() {
        const opts = currentOptions();
        return variants.find((v) => v.options.every((o, i) => o === opts[i]));
      }

      function update() {
        const variant = match();
        if (!variant) {
          if (addBtnEl) addBtnEl.setAttribute('aria-disabled', 'true');
          if (addBtn) addBtn.textContent = 'Unavailable';
          if (stockNote) stockNote.hidden = true;
          return;
        }
        if (idInput) idInput.value = variant.id;
        updateStockNote(variant);
        // Selected-value labels
        root.querySelectorAll('[data-selected-value]').forEach((el) => {
          const idx = parseInt(el.dataset.selectedValue, 10);
          el.textContent = variant.options[idx];
        });
        if (priceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceEl.innerHTML =
              '<span class="price price--sale">' + money(variant.price) + '</span>' +
              '<span class="price price--compare">' + money(variant.compare_at_price) + '</span>';
          } else {
            priceEl.innerHTML = '<span class="price">' + money(variant.price) + '</span>';
          }
        }
        if (addBtnEl && addBtn) {
          if (variant.available) {
            addBtnEl.removeAttribute('aria-disabled');
            addBtn.textContent = addBtnEl.dataset.labelAvailable || 'Reserve yours';
          } else {
            addBtnEl.setAttribute('aria-disabled', 'true');
            addBtn.textContent = addBtnEl.dataset.labelSoldout || 'Reserved out';
          }
        }
        // Reflect in URL for shareable variant links
        if (history.replaceState) {
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      }

      root.addEventListener('change', update);
      update();
    });
  }

  /* ----- Size guide ------------------------------------------------------ */
  function initSizeGuide() {
    const modal = document.querySelector('[data-size-guide]');
    if (!modal) return;

    const panel = modal.querySelector('[data-size-guide-panel]');
    const openers = document.querySelectorAll('[data-size-guide-open]');
    if (!openers.length || !panel) return;

    // Restored on close so keyboard users land back where they started.
    let lastFocused = null;

    const focusablesIn = (el) =>
      Array.from(
        el.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
      ).filter((n) => n.offsetParent !== null || n === el);

    function open() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      panel.focus();
    }

    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openers.forEach((btn) => btn.addEventListener('click', open));
    modal.querySelectorAll('[data-size-guide-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
      if (modal.hidden) return;
      if (e.key === 'Escape') {
        close();
        return;
      }
      // Trap Tab inside the dialog while it is open.
      if (e.key !== 'Tab') return;
      const items = focusablesIn(panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ----- Boot ------------------------------------------------------------ */
  function boot() {
    initReveal();
    initNav();
    initCart();
    initQuantitySteppers();
    initVariants();
    initSizeGuide();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
